import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Info, ExternalLink } from 'lucide-react';

/**
 * Generic component for displaying entity deletion errors with dependency information
 * 
 * @param {Object} props
 * @param {string} props.error - The error message from the API
 * @param {Function} props.onRetry - Optional callback for retry action
 * @param {string} props.entityType - The type of entity (e.g., 'Customer', 'Lead', 'Opportunity')
 * @param {string} props.entityName - The display name of the entity (e.g., 'customer', 'lead') - defaults to entityType lowercase
 * @param {Array} props.relatedEntities - Array of related entity types that might block deletion
 *   Example: [
 *     { type: 'Lead', path: '/leads', labelKey: 'nav.leads', resolveKey: 'resolveLeads' },
 *     { type: 'Opportunity', path: '/opportunities', labelKey: 'nav.opportunities', resolveKey: 'resolveOpportunities' }
 *   ]
 * @param {Object} props.translations - Optional custom translation keys
 *   Example: {
 *     cannotDelete: 'customers.cannotDeleteCustomer',
 *     dependenciesFound: 'customers.dependenciesFound',
 *     dependenciesExplanation: 'customers.dependenciesExplanation',
 *     howToResolve: 'customers.howToResolve',
 *     retryAfterResolving: 'customers.retryAfterResolving'
 *   }
 * @param {string} props.errorPattern - Optional custom error pattern to detect dependency errors
 */
const EntityDeleteError = ({ 
  error, 
  onRetry,
  entityType = 'Entity',
  entityName = null,
  relatedEntities = [],
  translations = {},
  errorPattern = null
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Default entity name from entityType if not provided
  const displayEntityName = entityName || entityType.toLowerCase();
  
  // Default error pattern - tries to match "Cannot delete {entity} because they/it has..."
  const defaultErrorPattern = errorPattern || `Cannot delete ${displayEntityName}`;
  const isDependencyError = error && error.toLowerCase().includes(defaultErrorPattern.toLowerCase());
  
  // Default translation keys
  const defaultTranslations = {
    cannotDelete: `common.cannotDelete${entityType}`,
    dependenciesFound: 'common.dependenciesFound',
    dependenciesExplanation: 'common.dependenciesExplanation',
    howToResolve: 'common.howToResolve',
    retryAfterResolving: 'common.retryAfterResolving'
  };
  
  // Merge custom translations with defaults
  const tKeys = { ...defaultTranslations, ...translations };
  
  // Default related entities if not provided (for backwards compatibility)
  const defaultRelatedEntities = relatedEntities.length > 0 ? relatedEntities : [
    { type: 'Lead', path: '/leads', labelKey: 'nav.leads', resolveKey: 'common.resolveLeads' },
    { type: 'Opportunity', path: '/opportunities', labelKey: 'nav.opportunities', resolveKey: 'common.resolveOpportunities' },
    { type: 'Contract', path: '/contracts', labelKey: 'nav.contracts', resolveKey: 'common.resolveContracts' },
    { type: 'Invoice', path: '/invoices', labelKey: 'nav.invoices', resolveKey: 'common.resolveInvoices' }
  ];

  if (!isDependencyError) {
    // For non-dependency errors, use the standard error display
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{t('common.error')}</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-3 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
              >
                {t('common.retry')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Extract the dependencies from the error message
  // Try multiple patterns to be flexible with error message formats
  const extractDependencies = (errorMessage) => {
    // Pattern 1: "they have X, Y, Z associated with them"
    let match = errorMessage.match(/they have (.+?) associated with them/i);
    if (match) {
      return match[1].split(',').map(d => d.trim());
    }
    
    // Pattern 2: "it has X, Y, Z associated with it"
    match = errorMessage.match(/it has (.+?) associated with it/i);
    if (match) {
      return match[1].split(',').map(d => d.trim());
    }
    
    // Pattern 3: "has X, Y, Z"
    match = errorMessage.match(/has (.+?)(?:\.|$)/i);
    if (match) {
      return match[1].split(',').map(d => d.trim());
    }
    
    // Pattern 4: Look for common entity types in the message
    const commonEntities = ['leads', 'opportunities', 'contracts', 'invoices', 'activities', 'customers'];
    const foundEntities = commonEntities.filter(entity => 
      errorMessage.toLowerCase().includes(entity)
    );
    
    if (foundEntities.length > 0) {
      return foundEntities.map(e => e.charAt(0).toUpperCase() + e.slice(1));
    }
    
    return [];
  };

  const dependencies = extractDependencies(error);

  // Filter related entities to only show those that appear in dependencies
  const relevantEntities = defaultRelatedEntities.filter(entity => {
    if (dependencies.length === 0) return true; // Show all if we can't parse dependencies
    return dependencies.some(dep => 
      dep.toLowerCase().includes(entity.type.toLowerCase()) ||
      entity.type.toLowerCase().includes(dep.toLowerCase())
    );
  });

  // If no relevant entities found but we have dependencies, show them anyway
  const entitiesToShow = relevantEntities.length > 0 ? relevantEntities : defaultRelatedEntities;

  return (
    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
      <div className="flex items-start space-x-3">
        <AlertTriangle className="h-6 w-6 text-orange-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">
            {t(tKeys.cannotDelete, `Cannot delete ${displayEntityName}`)}
          </h3>
          
          <div className="mt-3 bg-orange-100 dark:bg-orange-900/40 rounded-md p-4">
            <div className="flex items-start space-x-2">
              <Info className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-orange-800 dark:text-orange-200 mb-2">
                  {t(tKeys.dependenciesFound, 'Dependencies Found')}
                </h4>
                <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
                  {t(tKeys.dependenciesExplanation, `This ${displayEntityName} cannot be deleted because it has related records that must be removed first.`)}
                </p>
                
                {dependencies.length > 0 && (
                  <div className="space-y-2">
                    {dependencies.map((dependency, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-orange-400 dark:bg-orange-500 rounded-full flex-shrink-0"></div>
                        <span className="text-sm text-orange-800 dark:text-orange-200 font-medium">{dependency}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {entitiesToShow.length > 0 && (
            <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                {t(tKeys.howToResolve, 'How to Resolve')}
              </h4>
              <div className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                {entitiesToShow.map((entity, index) => (
                  <p key={index}>• {t(entity.resolveKey, `Remove or update related ${entity.type.toLowerCase()}`)}</p>
                ))}
              </div>
              
              <div className="mt-3 flex flex-wrap gap-2">
                {entitiesToShow.map((entity, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(entity.path)}
                    className="inline-flex items-center space-x-1 text-xs bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 text-blue-800 dark:text-blue-200 px-2 py-1 rounded transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>{t(entity.labelKey, entity.type)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {onRetry && (
            <div className="mt-4">
              <button
                onClick={onRetry}
                className="text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200 underline"
              >
                {t(tKeys.retryAfterResolving, 'Retry after resolving dependencies')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntityDeleteError;

